import { getUserProfileById } from "../../services/api";
import * as types from "../types";

const userProfileRequest = () => ({ type: types.USER_PROFILE_REQUEST });
export const userProfileSuccess = (data) => ({
  type: types.USER_PROFILE_SUCCESS,
  payload: data,
});
const userProfileFailure = (error) => ({
  type: types.USER_PROFILE_FAILURE,
  payload: error,
});

// routes.js's app-shell effect and Gallery/index.jsx's own mount effect both
// dispatch this thunk for the same logged-in userId on first render of
// /gallery/:id, ~500-1500ms apart (staggered by lazy-chunk load time, not
// truly concurrent) — too far apart for the axios transport's in-flight GET
// coalescer (services/request.js) to catch, so it still hit the network
// twice. Dedupe in-flight fetches here, keyed by userId, so the second
// dispatch reuses the first's promise instead of firing a second request.
const inFlightProfileFetches = new Map();

export const getUserProfileByIdAction = (userId) => {
  return async (dispatch) => {
    dispatch(userProfileRequest());
    try {
      let fetchPromise = inFlightProfileFetches.get(userId);
      if (!fetchPromise) {
        fetchPromise = getUserProfileById(userId);
        inFlightProfileFetches.set(userId, fetchPromise);
        fetchPromise.finally(() => inFlightProfileFetches.delete(userId));
      }
      const response = await fetchPromise;
      dispatch(userProfileSuccess(response.data));
    } catch (error) {
      dispatch(userProfileFailure(error.message));
    }
  };
};

// fix-fe-5107-own-profile-duplicate-fetch-regressed — the in-flight dedupe
// above only coalesces calls that overlap WHILE a fetch is outstanding; once
// it settles, the map entry is gone and the NEXT dispatch starts a brand new
// network request no matter how recently the same profile was already
// loaded. #5107 patched exactly one such pair (routes.js + Gallery/index.jsx
// own mount effect), but GalleryHeader.jsx's own mount effect ALSO dispatches
// this same thunk for the same loggedInUserId, and it (like the others)
// mounts on its own schedule — outside every other call site's in-flight
// window on a real, chunk-loading page. MEASURED live on web-dev.iyuga.net
// (bundle main.d2a00e47.js): navigating to the account's own /gallery/:id
// fired GET /api/v1/user/{id}/profile/ 3-5 times per run, staggered 0-12s
// apart, one per independent mount site — not a single race anymore, a
// systemic gap. Passive, mount-time callers (routes.js, GalleryHeader.jsx,
// Gallery/index.jsx) don't need a guaranteed-fresh fetch — they just want
// "make sure redux has this user's profile". Mirrors the exact
// already-have-it check Gallery/index.jsx's own shouldSkipOwnProfileFetch
// (galleryLoadState.js) already uses to gate its react-query hook, now
// centralized here so EVERY passive mount site benefits, not just one
// component. Callers that need a guaranteed refetch after a mutation (e.g.
// Referrals/index.jsx after a redeem) keep calling getUserProfileByIdAction
// directly and are unaffected.
export const getOwnProfileIfNeededAction = (userId) => {
  return (dispatch, getState) => {
    const existingProfile = getState().userProfile?.userProfile;
    // fix-from-drive-verify-fe-5178 (3rd source): this guard checked
    // `existingProfile?.data?.user_id`, but `USER_PROFILE_SUCCESS`
    // (redux/reducers/user.js) stores `{ ...action.payload }` FLAT —
    // `action.payload` IS `response.data` from `getUserProfileById`
    // (services/api.js), a single axios envelope with `user_id` at the top
    // level, not nested under a second `.data`. Every other real call site
    // reads it flat (`userProfile?.user_id` — see Gallery/index.jsx:557,
    // Referrals/index.jsx, ShareInviteModal/index.jsx). Because `.data` is
    // always undefined on the real object, this comparison was always
    // false, so the guard never actually skipped anything — every passive
    // mount site (routes.js, GalleryHeader.jsx, Gallery/index.jsx) refetched
    // for real once its own effect ran outside the others' in-flight window,
    // which is exactly the residual 2x `GET .../profile/` MEASURED live on
    // web-dev.iyuga.net after PR#5178/PR#5189 (both of which only fixed
    // concurrent-call coalescing, not this always-false guard).
    // eslint-disable-next-line eqeqeq -- existingProfile.user_id is a number from the API; userId here is sometimes a route-param string
    if (existingProfile?.user_id == userId) {
      return Promise.resolve();
    }
    return dispatch(getUserProfileByIdAction(userId));
  };
};

export const editUserProfileAction = (userData) => ({
  type: types.EDIT_USER_PROFILE_SUCCESS,
  payload: userData,
});

export const logout = () => ({
  type: types.LOGOUT,
});

export const setCheckboxLinkedWheel = (checkboxValue) => ({
  type: types.SET_CHECKBOX_LINKED_WHEEL,
  payload: checkboxValue,
});

export const setReportModalOpen = (modalStatus) => ({
  type: types.SET_REPORT_MODAL_OPEN,
  payload: modalStatus,
});

export const setReportInstance = (instance) => ({
  type: types.SET_REPORT_INSTANCE,
  payload: instance,
});

export const setReportLocation = (location) => ({
  type: types.SET_REPORT_LOCATION,
  payload: location,
});
